import React from 'react';

function InvestmentManagement() {
  return (
    <main className="bg-white">
      <section className="relative h-[42vh] min-h-[320px] flex items-center justify-center overflow-hidden">
        <img src="/images/real-people/teamwork-planning-happy-business-people-meeting-together-group-office-project-smile-mission-employees-listening-with-interest-collaboration-online-strategy-agency-goals_590464-158051.jpg" alt="Investment Management" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center px-6">
          <h1 className="text-white text-4xl sm:text-5xl font-semibold tracking-tight">Investment Management</h1>
          <p className="mt-3 text-white/90 max-w-2xl mx-auto">Separately managed accounts tailored to goals, constraints, and risk tolerance.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Mandate Design</h2>
            <p className="mt-4 text-gray-600">We formalize investment objectives, drawdown limits, eligible assets, and rebalancing rules. Portfolios use multi-factor selection, tactical tilts, and risk overlays.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {['Core Equity','Credit Income','Macro Trend','Market Neutral'].map(x => (
                <div key={x} className="rounded-xl border border-gray-100 p-4 bg-white shadow-sm text-gray-800 text-sm font-medium">{x}</div>
              ))}
            </div>
          </div>
          <img src="/images/two-stock-exchange-traders-discussing-dynamic-investment-graph-sellable_31965-313820.jpg_yeupoj.jpg" alt="Mandate" className="w-full h-80 object-cover rounded-xl shadow" />
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { t: 'Research', d: 'Macroeconomic, sector, valuation, and chain analytics.' },
            { t: 'Execution', d: 'VWAP/TWAP algos, smart routing, and liquidity aware.' },
            { t: 'Oversight', d: 'Daily risk, weekly committees, quarterly mandate reviews.' }
          ].map((x) => (
            <div key={x.t} className="rounded-xl bg-white p-6 shadow border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
              <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <img src="/images/real-people/business-meeting-office_1268-21537.png" alt="Reporting" className="w-full h-80 object-cover rounded-xl shadow" />
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Reporting & Transparency</h2>
            <p className="mt-4 text-gray-600">Performance is reported net of fees with benchmark comparisons. We provide exposures, attribution, and risk metrics including VaR, beta, and drawdown.</p>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6">
          {[
            { t: 'Individuals & Families', d: 'Goal‑based portfolios, preservation, and long‑term growth.' },
            { t: 'Institutions', d: 'Liability‑aware mandates, policy alignment, and reporting rigor.' },
            { t: 'Corporates', d: 'Treasury optimization, liquidity sleeves, and bespoke constraints.' }
          ].map((x) => (
            <div key={x.t} className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm hover:shadow-md transition duration-300">
              <h3 className="text-lg font-semibold text-gray-900">{x.t}</h3>
              <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Portfolio Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Portfolio Solutions</h2>
          <p className="mt-3 text-gray-600 max-w-3xl">Institutional discipline adapted to your mandate. We combine strategic allocations with dynamic risk controls and clear rebalancing rules.</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { t: 'Core Equity', d: 'Quality, profitability, and valuation screens with sector neutrality.' },
              { t: 'Fixed Income', d: 'Duration‑aware credit across sovereign, corporate, and securitized.' },
              { t: 'Multi‑Asset', d: 'Long‑term policy mix with tactical tilts around macro regime.' },
              { t: 'Income & Dividend', d: 'Equity income and credit sleeves for dependable cash flows.' },
              { t: 'Alternatives Sleeve', d: 'Market‑neutral, trend, and carry to diversify equity beta.' },
              { t: 'Impact & ESG', d: 'Mandates aligned to sustainability metrics and stewardship.' }
            ].map((x) => (
              <div key={x.t} className="rounded-xl bg-white border border-gray-100 p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition duration-300">
                <h3 className="text-base font-semibold text-gray-900">{x.t}</h3>
                <p className="mt-2 text-gray-600 text-sm">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disciplined Process */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Disciplined Process</h2>
            <ol className="mt-6 space-y-5">
              {[
                { t: 'Discovery', d: 'Objectives, constraints, liquidity, horizon, and risk tolerance.' },
                { t: 'Design', d: 'Strategic mix, factor sleeves, and rebalancing bands.' },
                { t: 'Implementation', d: 'Low‑slippage execution with venue and liquidity awareness.' },
                { t: 'Risk & Oversight', d: 'Ex‑ante limits, stress tests, drift monitoring, and drawdown guards.' },
                { t: 'Review', d: 'Attribution, benchmark alignment, and mandate refinements.' }
              ].map((x, i) => (
                <li key={x.t} className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-semibold">{i + 1}</span>
                  <div>
                    <p className="font-semibold text-gray-900">{x.t}</p>
                    <p className="text-gray-600 text-sm mt-0.5">{x.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <img src="/images/real-people/inspiring-innovation-with-their-every-move-cropped-shot-group-businesspeople-working-together-office_590464-10869.jpg" alt="Process" className="w-full h-96 object-cover rounded-xl shadow" />
        </div>
      </section>

      {/* Risk Management */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Risk Management</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { t: 'Volatility Bands', d: 'Position sizing adapts to realized and implied volatility.' },
              { t: 'Drawdown Guards', d: 'Rules to reduce risk after threshold losses.' },
              { t: 'Exposures', d: 'Beta, factor, and concentration thresholds enforced pre‑trade.' },
              { t: 'Scenarios', d: 'Rates shocks, spread widening, and cross‑asset stress tests.' }
            ].map((x) => (
              <div key={x.t} className="rounded-xl border border-green-100 bg-green-50 p-5 text-gray-800">
                <p className="font-semibold text-green-700">{x.t}</p>
                <p className="mt-1 text-sm text-gray-700">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees & Minimums */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-3 gap-6 items-start">
          <div className="rounded-xl bg-white border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Advisory Fees</h3>
            <p className="mt-2 text-gray-600 text-sm">Tiered, asset‑based fees; performance fees available where suitable.</p>
          </div>
          <div className="rounded-xl bg-white border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Minimums</h3>
            <p className="mt-2 text-gray-600 text-sm">Varies by strategy and vehicle. SMA minimums typically apply.</p>
          </div>
          <div className="rounded-xl bg-white border border-gray-100 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Custody</h3>
            <p className="mt-2 text-gray-600 text-sm">Assets held with reputable third‑party custodians; full transparency.</p>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">FAQs</h2>
          <div className="mt-6 space-y-3">
            {[
              { q: 'What benchmarks do you use?', a: 'We agree a policy benchmark per mandate; custom composites supported.' },
              { q: 'How often do you rebalance?', a: 'Rules‑based thresholds with periodic reviews; drift and cost aware.' },
              { q: 'Can you manage ESG constraints?', a: 'Yes. Negative screens, tilts, and engagement available by design.' },
              { q: 'What reports do clients receive?', a: 'Performance, attribution, exposures, and risk including VaR and drawdown.' }
            ].map((x) => (
              <details key={x.q} className="group rounded-lg border border-gray-100 p-4 open:bg-gray-50 transition">
                <summary className="list-none cursor-pointer select-none flex items-center justify-between">
                  <span className="font-medium text-gray-900">{x.q}</span>
                  <span className="text-gray-400 group-open:rotate-180 transition">▾</span>
                </summary>
                <p className="mt-2 text-sm text-gray-600">{x.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-xs text-gray-500">Past performance is not a guarantee of future results. Investing involves risk, including possible loss of principal.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-semibold">Discuss a managed mandate</h3>
          <p className="mt-2 text-white/80">We will propose a portfolio aligned to your objectives.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="/apply" className="px-6 py-3 rounded-md bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] text-black font-semibold">Apply now</a>
            <a href="/contact" className="px-6 py-3 rounded-md border border-white/30">Contact us</a>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InvestmentManagement;

